import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload,
  FileText,
  Loader2,
  Trash2,
  MessageSquare,
  Table2,
} from "lucide-react";
import clsx from "clsx";
import { api, postForm } from "../api";
import { StatusBadge, statusLabel, EmptyState, Button } from "../ui";
import { useToast } from "../ui/toast";
import { useModal } from "../ui/modal";
import { katalog, useT } from "../i18n";
import DatasetWizard from "../components/DatasetWizard";

type SummaryStatus = "pending" | "processing" | "done" | "error";

interface DocOut {
  id: string;
  title: string;
  source_pdf_url: string | null;
  embedding_status: string;
  summary: string | null;
  summary_status: SummaryStatus;
  chunk_count: number;
  project_id: string | null;
  created_at: string;
}

interface ChatRow {
  id: string;
}

export default function DocumentsPage() {
  const [docs, setDocs] = useState<DocOut[]>([]);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [wizardDoc, setWizardDoc] = useState<DocOut | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const modal = useModal();
  const navigate = useNavigate();
  const t = useT();

  // `katalog()` statt `t`: dieses `load` wird vom Intervall unten (Deps []) aus
  // dem ERSTEN Render festgehalten. Mit `t` bliebe die Meldung nach einem
  // Sprachwechsel auf dem alten Katalog stehen (docs/i18n.md §3).
  const load = async () => {
    try {
      setDocs(await api.get<DocOut[]>("/documents?limit=100"));
    } catch (e) {
      toast.err(katalog().documents.ladenFehlgeschlagen, (e as Error).message);
    }
  };

  useEffect(() => {
    load();
    // Auto-Refresh, solange noch was wartet
    const t = setInterval(() => {
      setDocs((current) => {
        if (
          current.some(
            (d) =>
              d.embedding_status === "pending" ||
              d.embedding_status === "processing" ||
              d.summary_status === "pending" ||
              d.summary_status === "processing"
          )
        ) {
          load();
        }
        return current;
      });
    }, 4000);
    return () => clearInterval(t);
  }, []);

  const onFile = async (file: File) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await postForm<DocOut>("/documents", fd);
      toast.ok(t.documents.hochgeladenToast, t.documents.embeddingLaeuft(file.name));
      await load();
    } catch (e) {
      toast.err(t.documents.uploadFehlgeschlagen, (e as Error).message);
    } finally {
      setBusy(false);
      setOver(false);
    }
  };

  const delDoc = async (id: string, title: string) => {
    const ok = await modal.confirm({
      title: t.documents.wirklichLoeschen(title),
      description: t.documents.chunksUndVektoren,
      confirmLabel: t.common.loeschen,
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.ok(t.documents.dokumentGeloescht);
      setDocs((s) => s.filter((d) => d.id !== id));
    } catch (e) {
      toast.err(t.documents.loeschenFehlgeschlagen, (e as Error).message);
    }
  };

  const chatAbout = async (doc: DocOut) => {
    try {
      const chat = await api.post<ChatRow>("/chats", {
        project_id: doc.project_id,
        // Chat-TITEL (Inhalt, kein Etikett), deutsch wie die noch nicht
        // umgestellte Chat-Oberfläche (docs/i18n.md §5) — nicht wegen der
        // Auto-Betitelung, die seit 680ee5e der Einstellung folgt.
        title: `Über: ${doc.title}`,
      });
      navigate(`/chat/${chat.id}`);
    } catch (e) {
      toast.err(t.documents.chatErstellungFehlgeschlagen, (e as Error).message);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">{t.documents.seitenTitel}</h1>
        <p className="text-sm text-text-2 mt-1">{t.documents.untertitel}</p>
      </header>

      <label
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f) onFile(f);
        }}
        className={clsx(
          "block bg-surface border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors",
          over
            ? "border-brand-600 bg-brand-50/50 dark:bg-brand-700/10"
            : "border-border-strong hover:border-brand-500"
        )}
      >
        <Upload className="w-7 h-7 text-brand-500 mx-auto mb-2" />
        <div className="text-sm font-medium">
          {t.documents.dateiZiehen}
        </div>
        <div className="text-xs text-text-2 mt-1">{t.documents.dateiTypen}</div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.md,.markdown"
          aria-label={t.documents.uploadAria}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
          }}
          className="sr-only"
        />
        {busy && (
          <div className="mt-3 inline-flex items-center gap-2 text-xs text-text-2">
            <Loader2 className="w-3 h-3 animate-spin" /> {t.documents.wirdHochgeladen}
          </div>
        )}
      </label>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-3">
          {t.documents.seitenTitel}{" "}
          <span className="text-text-3 font-normal">({docs.length})</span>
        </h2>
        <div className="space-y-3">
          {docs.length === 0 && (
            <div className="bg-surface border border-border rounded-lg">
              <EmptyState
                icon={<FileText className="w-6 h-6" />}
                title={t.documents.keineDokumente}
                hint={t.documents.keineDokumenteHinweis}
                action={
                  <Button
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t.documents.dateiAuswaehlen}
                  </Button>
                }
              />
            </div>
          )}
          {docs.map((d) => (
            <DocCard
              key={d.id}
              doc={d}
              onDelete={() => delDoc(d.id, d.title)}
              onChat={() => chatAbout(d)}
              onTable={() => setWizardDoc(d)}
              onOpen={() => navigate(`/docs/${d.id}`)}
            />
          ))}
        </div>
      </section>

      <DatasetWizard
        open={wizardDoc !== null}
        onClose={() => setWizardDoc(null)}
        doc={
          wizardDoc
            ? { id: wizardDoc.id, title: wizardDoc.title, project_id: wizardDoc.project_id }
            : null
        }
      />
    </div>
  );
}

function DocCard({
  doc,
  onDelete,
  onChat,
  onTable,
  onOpen,
}: {
  doc: DocOut;
  onDelete: () => void;
  onChat: () => void;
  onTable: () => void;
  onOpen: () => void;
}) {
  const t = useT();
  return (
    <div className="bg-surface border border-border rounded-lg p-4 group hover:border-brand-500/40 transition-colors">
      <div className="flex items-start gap-3">
        <FileText className="w-4 h-4 text-text-2 mt-0.5 shrink-0" />
        <button
          onClick={onOpen}
          className="flex-1 min-w-0 text-left"
        >
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium truncate flex-1 group-hover:text-brand-600">
              {doc.title}
            </div>
            <StatusPill s={doc.embedding_status} />
          </div>
          <div className="text-xs text-text-2 mt-0.5">
            {new Date(doc.created_at).toLocaleDateString("de-DE")}
          </div>
          <SummaryBlock doc={doc} />
        </button>
        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity">
          <button
            onClick={onTable}
            className="p-1.5 text-text-2 hover:text-success hover:bg-success-soft rounded-md"
            title={t.documents.tabelleAusDokument}
            disabled={doc.embedding_status !== "done"}
          >
            <Table2 className="w-4 h-4" />
          </button>
          <button
            onClick={onChat}
            className="p-1.5 text-text-2 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20 rounded-md"
            title={t.documents.mitDokumentUnterhalten}
          >
            <MessageSquare className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-text-2 hover:text-danger hover:bg-danger-soft rounded-md"
            title={t.common.loeschen}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryBlock({ doc }: { doc: DocOut }) {
  const t = useT();
  if (doc.summary_status === "pending" || doc.summary_status === "processing") {
    return (
      <div className="mt-2 text-xs text-text-3 italic inline-flex items-center gap-1">
        <Loader2 className="w-3 h-3 animate-spin" /> {t.documents.zusammenfassungWirdGeneriert}
      </div>
    );
  }
  if (doc.summary_status === "error") {
    return (
      <div className="mt-2 text-xs text-danger">{t.documents.zusammenfassungFehlgeschlagen}</div>
    );
  }
  if (!doc.summary) return null;
  return (
    <div className="mt-2 text-sm text-text-2 leading-relaxed line-clamp-4">
      {doc.summary}
    </div>
  );
}

function StatusPill({ s }: { s: string }) {
  const info = statusLabel(s, { domain: "document" });
  return (
    <StatusBadge status={info.status} className="shrink-0">
      {info.label}
    </StatusBadge>
  );
}
